import { useRef, useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Upload, X } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import { useT } from "@/lib/i18n";

const probeImage = (file) => new Promise((res) => {
  const img = new Image();
  img.onload = () => { res({ w: img.naturalWidth, h: img.naturalHeight }); URL.revokeObjectURL(img.src); };
  img.onerror = () => res(null);
  img.src = URL.createObjectURL(file);
});

const probeVideo = (file) => new Promise((res) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { res({ w: v.videoWidth, h: v.videoHeight }); URL.revokeObjectURL(v.src); };
  v.onerror = () => res(null);
  v.src = URL.createObjectURL(file);
});

const is169 = ({ w, h }) => Math.abs(w / h - 16 / 9) < 0.02;

const check = async (kind, file, t) => {
  if (kind === "photo") {
    const d = await probeImage(file);
    if (!d) return t("up.photoRead");
    if (d.w < 1920 || d.h < 1080 || !is169(d)) return t("up.photoSize", { s: `${d.w}×${d.h}` });
  }
  if (kind === "video") {
    const d = await probeVideo(file);
    if (d && d.w && (d.w !== 1920 || d.h !== 1080)) return t("up.videoSize", { s: `${d.w}×${d.h}` });
  }
  return null;
};

const mb = (n) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

export const FileUpload = ({ kind, accept, label, value, onChange, testid }) => {
  const { t } = useT();
  const ref = useRef();
  const [pct, setPct] = useState(null);
  const [err, setErr] = useState("");

  const pick = async (file) => {
    if (!file) return;
    setErr("");
    const bad = await check(kind, file, t);
    if (bad) { setErr(bad); return; }
    try {
      setPct(0);
      const { data: init } = await axios.post(`${API}/public/uploads`, { kind, filename: file.name, size: file.size });
      for (let i = 0; i < init.parts; i++) {
        const blob = file.slice(i * init.chunk_size, (i + 1) * init.chunk_size);
        for (let attempt = 0; ; attempt++) {
          try {
            await axios.put(`${API}/public/uploads/${init.id}/${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
            break;
          } catch (e) { if (attempt >= 2) throw e; }
        }
        setPct(Math.round(((i + 1) / init.parts) * 100));
      }
      const { data } = await axios.post(`${API}/public/uploads/${init.id}/complete`);
      onChange(data);
    } catch (e) { setErr(errMsg(e)); onChange(null); }
    finally { setPct(null); if (ref.current) ref.current.value = ""; }
  };

  return (
    <div data-testid={testid}>
      <input ref={ref} type="file" accept={accept} className="hidden" data-testid={`${testid}-input`} onChange={(e) => pick(e.target.files[0])} />
      {value ? (
        <div className="flex items-center gap-2 rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="flex-1 truncate" data-testid={`${testid}-name`}>{value.filename} · {mb(value.size)}</span>
          <button type="button" data-testid={`${testid}-remove`} onClick={() => onChange(null)} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <button type="button" disabled={pct !== null} data-testid={`${testid}-btn`} onClick={() => ref.current.click()}
          className="w-full h-12 rounded-xl border border-dashed border-blue-500/40 hover:bg-blue-500/10 text-sm text-blue-200 flex items-center justify-center gap-2 disabled:opacity-70">
          {pct !== null ? <><Loader2 className="w-4 h-4 animate-spin" />{t("up.sending")} {pct}%</> : <><Upload className="w-4 h-4" />{label}</>}
        </button>
      )}
      {pct !== null && <div className="h-1.5 rounded-full bg-white/5 mt-2 overflow-hidden"><div className="h-full bg-blue-500 transition-[width]" style={{ width: `${pct}%` }} /></div>}
      {err && <p className="text-xs text-red-400 mt-1" data-testid={`${testid}-error`}>{err}</p>}
    </div>
  );
};
